import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import scroller from 'src/js/modules/scroller';
import constants from '../constants';

const { Stack, Paragraph, CtaBanner, Box, ControlGroup, CodeEditorField, FieldStatus } =
  designSystem;

const { scrollToElementInContainer } = scroller.helpers;
const {
  common: { POPOVER_ANIMATION_TIME },
  fieldNames,
  CODE_EDITOR_HEIGHT,
} = constants;

function CustomCodeSection({
  control,
  captions,
  scrollContainerRef,
  isHeadCodeDisabled,
  isHeaderCodeDisabled,
  isFooterCodeDisabled,
  onCodeEditorFocus,
  onHeadCodeUpgradeClick,
  onHeaderCodeUpgradeClick,
  onFooterCodeUpgradeClick,
}) {
  const [activeUpgradePopover, setActiveUpgradePopover] = useState(null);
  const headCodeUpgradePopoverRef = useRef();
  const headerCodeUpgradePopoverRef = useRef();
  const footerCodeUpgradePopoverRef = useRef();

  const handleCodeEditorClick = useCallback(
    (codeFieldName, popoverRef, codeFieldsDisabled) => {
      if (codeFieldsDisabled) {
        const element = popoverRef.current;

        scrollToElementInContainer(scrollContainerRef.current, element).then(() => {
          setActiveUpgradePopover(codeFieldName);
          setTimeout(() => setActiveUpgradePopover(null), POPOVER_ANIMATION_TIME);
        });
      }
    },
    [scrollContainerRef]
  );

  const handleHeadCodeClick = useCallback(
    () =>
      handleCodeEditorClick(fieldNames.HEAD_CODE, headCodeUpgradePopoverRef, isHeadCodeDisabled),
    [handleCodeEditorClick, headCodeUpgradePopoverRef, isHeadCodeDisabled]
  );

  const handleHeaderCodeClick = useCallback(
    () =>
      handleCodeEditorClick(
        fieldNames.HEADER_CODE,
        headerCodeUpgradePopoverRef,
        isHeaderCodeDisabled
      ),
    [handleCodeEditorClick, headerCodeUpgradePopoverRef, isHeaderCodeDisabled]
  );

  const handleFooterCodeClick = useCallback(
    () =>
      handleCodeEditorClick(
        fieldNames.FOOTER_CODE,
        footerCodeUpgradePopoverRef,
        isFooterCodeDisabled
      ),
    [handleCodeEditorClick, footerCodeUpgradePopoverRef, isFooterCodeDisabled]
  );

  return (
    <Stack gap="spacing-xs">
      <Paragraph appearance="medium-emphasis" size="small">
        {captions.description}
      </Paragraph>

      <div>
        <ControlGroup title={captions.headCode.name}>
          <div onClick={handleHeadCodeClick}>
            <CodeEditorField
              name={fieldNames.HEAD_CODE}
              control={control}
              disabled={isHeadCodeDisabled}
              height={isHeadCodeDisabled ? CODE_EDITOR_HEIGHT.min : CODE_EDITOR_HEIGHT.max}
              onFocus={onCodeEditorFocus}
              options={{
                readOnly: isHeadCodeDisabled,
                showPrintMargin: true,
                showLineNumbers: true,
                tabSize: 2,
                autoScrollEditorIntoView: true,
              }}
            />
          </div>
        </ControlGroup>
        {isHeadCodeDisabled && (
          <Box marginTop="spacing-3-xs" marginBottom="spacing-3-xs">
            <div ref={headCodeUpgradePopoverRef}>
              <CtaBanner
                title={captions.headCode.upgradeText.description}
                iconGlyph="upgrade"
                animate={activeUpgradePopover === fieldNames.HEAD_CODE}
                id={fieldNames.HEAD_CODE}
                ctaButton={{
                  label: captions.headCode.upgradeText.upgrade,
                  onClick: onHeadCodeUpgradeClick,
                }}
              />
            </div>
          </Box>
        )}
        <FieldStatus text={captions.headCode.description} />
      </div>

      <div>
        <ControlGroup title={captions.headerCode.name}>
          <div onClick={handleHeaderCodeClick}>
            <CodeEditorField
              name={fieldNames.HEADER_CODE}
              control={control}
              disabled={isHeaderCodeDisabled}
              height={isHeaderCodeDisabled ? CODE_EDITOR_HEIGHT.min : CODE_EDITOR_HEIGHT.max}
              onFocus={onCodeEditorFocus}
              options={{
                readOnly: isHeaderCodeDisabled,
                showPrintMargin: true,
                showLineNumbers: true,
                tabSize: 2,
                autoScrollEditorIntoView: true,
              }}
            />
          </div>
        </ControlGroup>
        {isHeaderCodeDisabled && (
          <Box marginTop="spacing-3-xs" marginBottom="spacing-3-xs">
            <div ref={headerCodeUpgradePopoverRef}>
              <CtaBanner
                title={captions.headerCode.upgradeText.description}
                iconGlyph="upgrade"
                animate={activeUpgradePopover === fieldNames.HEADER_CODE}
                id={fieldNames.HEADER_CODE}
                ctaButton={{
                  label: captions.headerCode.upgradeText.upgrade,
                  onClick: onHeaderCodeUpgradeClick,
                }}
              />
            </div>
          </Box>
        )}
        <FieldStatus text={captions.headerCode.description} />
      </div>

      <div>
        <ControlGroup title={captions.footerCode.name}>
          <div onClick={handleFooterCodeClick}>
            <CodeEditorField
              name={fieldNames.FOOTER_CODE}
              control={control}
              disabled={isFooterCodeDisabled}
              height={isFooterCodeDisabled ? CODE_EDITOR_HEIGHT.min : CODE_EDITOR_HEIGHT.max}
              onFocus={onCodeEditorFocus}
              options={{
                readOnly: isFooterCodeDisabled,
                showPrintMargin: true,
                showLineNumbers: true,
                tabSize: 2,
                autoScrollEditorIntoView: true,
              }}
            />
          </div>
        </ControlGroup>
        {isFooterCodeDisabled && (
          <Box marginTop="spacing-3-xs" marginBottom="spacing-3-xs">
            <div ref={footerCodeUpgradePopoverRef}>
              <CtaBanner
                title={captions.footerCode.upgradeText.description}
                iconGlyph="upgrade"
                animate={activeUpgradePopover === fieldNames.FOOTER_CODE}
                id={fieldNames.FOOTER_CODE}
                ctaButton={{
                  label: captions.footerCode.upgradeText.upgrade,
                  onClick: onFooterCodeUpgradeClick,
                }}
              />
            </div>
          </Box>
        )}
        <FieldStatus text={captions.footerCode.description} />
      </div>
    </Stack>
  );
}

CustomCodeSection.propTypes = {
  control: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  scrollContainerRef: PropTypes.shape().isRequired,
  isHeaderCodeDisabled: PropTypes.bool.isRequired,
  isFooterCodeDisabled: PropTypes.bool.isRequired,
  isHeadCodeDisabled: PropTypes.bool.isRequired,
  onCodeEditorFocus: PropTypes.func.isRequired,
  onHeadCodeUpgradeClick: PropTypes.func.isRequired,
  onHeaderCodeUpgradeClick: PropTypes.func.isRequired,
  onFooterCodeUpgradeClick: PropTypes.func.isRequired,
};

export default CustomCodeSection;
